import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { mockData } from '../data/mock';

const SITE_URL = 'https://pnielanalytics.com';
const PUBLISHER = 'Pniel Analytics LLC';
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_KEYWORDS = 'business intelligence, data analytics, Tableau consulting, Power BI, data visualization, dashboards, analytics consulting, data strategy, Pniel Analytics';

const PAGE_SEO = {
  '/': {
    title: 'Pniel Analytics | Tableau & Power BI Consulting, Delaware',
    description: 'Pniel Analytics builds automated Tableau and Power BI dashboards for executive teams. Book a free 30-minute analytics assessment today.',
    keywords: DEFAULT_KEYWORDS,
  },
  '/services': {
    title: 'BI, Tableau & Power BI Consulting Services | Pniel Analytics',
    description: 'Explore Pniel Analytics services: BI & dashboard development, Tableau and Power BI consulting, data strategy & governance, and advanced predictive analytics.',
    keywords: 'analytics consulting services, BI consulting, Tableau consulting, Power BI consulting, data strategy, predictive analytics, Pniel Analytics',
  },
  '/government': {
    title: 'Government Data Analytics & BI Contractor | Pniel Analytics',
    description: 'SAM-registered small business delivering Tableau and Power BI dashboards for federal, state, and local agencies. View our capability statement.',
    keywords: 'government analytics contractor, SAM registered, GSA analytics, federal BI consultant, UEI, CAGE, NAICS, capability statement, Pniel Analytics',
  },
  '/services/bi-dashboards': {
    title: 'Tableau & Power BI Dashboard Development | Pniel Analytics',
    description: 'Tableau and Power BI dashboards built on clean, reliable data: executive dashboards and automated reporting that get answers faster. Book a free assessment.',
    keywords: 'BI dashboard development, Tableau dashboard design, Power BI consulting, executive dashboards, reporting automation, business intelligence consulting, data visualization',
  },
  '/services/data-strategy': {
    title: 'Data Strategy & Analytics Consulting | Pniel Analytics',
    description: 'Data strategy from Pniel Analytics: roadmaps, metric governance, integration, and enablement that turn fragmented systems into a reliable analytics foundation.',
    keywords: 'data strategy consulting, data roadmap, metric governance, data integration, analytics consulting, data governance, analytics training',
  },
  '/services/advanced-analytics': {
    title: 'Predictive Analytics & Forecasting | Pniel Analytics',
    description: 'Churn prediction, demand forecasting, and LTV models built on your existing data. See if you are ready with a free 30-minute analytics assessment.',
    keywords: 'advanced analytics, predictive analytics, forecasting, customer segmentation, predictive models, machine learning consulting, marketing analytics',
  },
  '/services/tableau-consulting': {
    title: 'Tableau Consulting in Delaware | Pniel Analytics',
    description: 'Tableau consulting in Newark, Delaware: executive dashboard design, SQL performance tuning, and reporting automation for Delaware & Mid-Atlantic teams.',
    keywords: 'Tableau consultant Delaware, Tableau consulting Delaware, Tableau consultant Wilmington, Tableau Newark DE, executive dashboards, performance tuning, reporting automation',
  },
  '/services/power-bi-consulting': {
    title: 'Power BI Consulting in Delaware | Pniel Analytics',
    description: 'Power BI consulting in Newark, Delaware: dashboards, DAX and data modeling, Microsoft Fabric, and governance for teams across Delaware and the Mid-Atlantic.',
    keywords: 'Power BI consultant Delaware, Power BI consulting Delaware, Power BI consultant Wilmington, Power BI Newark DE, DAX, Microsoft Fabric, Power BI governance',
  },
  '/contact': {
    title: 'Contact Pniel Analytics | Delaware BI Consulting',
    description: 'Contact Pniel Analytics in Newark, Delaware, for BI consulting, Tableau, Power BI, and data strategy services. Book your strategy call today.',
    keywords: 'contact Pniel Analytics, Delaware analytics consultant, Newark Delaware BI consulting, book analytics assessment',
  },
  '/delaware': {
    title: 'Tableau & Power BI Consultant in Delaware | Pniel Analytics',
    description: 'Newark, Delaware based Tableau and Power BI consulting: dashboards, data strategy, and analytics for Delaware businesses, on-site and remote nationwide.',
    keywords: 'Tableau consultant Delaware, Power BI consulting Delaware, Newark DE analytics, Wilmington BI consultant, Delaware dashboard developer, data analytics Delaware',
  },
  '/philadelphia': {
    title: 'Tableau & Power BI Consultant in Philadelphia',
    description: 'Tableau and Power BI consulting for the Greater Philadelphia area: dashboards, data strategy and analytics, available on-site and remote.',
    keywords: 'Tableau consultant Philadelphia, Power BI consulting Philadelphia, Philadelphia BI consultant, Main Line analytics, Philadelphia dashboard developer, data analytics Philadelphia',
  },
  '/careers': {
    title: 'Careers | Pniel Analytics',
    description: "Join the Pniel Analytics talent pool: data analysts, dashboard developers, and Tableau/Power BI consultants. Introduce yourself and we'll be in touch.",
    keywords: 'Pniel Analytics careers, data analyst jobs Delaware, BI developer jobs, Tableau consultant careers, Power BI jobs, analytics careers Newark DE',
  },
  '/about': {
    title: 'About Pniel Analytics | BI & Analytics Consulting, Delaware',
    description: 'Pniel Analytics is a Newark, Delaware BI and analytics consulting firm building Tableau and Power BI dashboards for private and public-sector teams.',
    keywords: 'about Pniel Analytics, Delaware BI consulting firm, Newark DE analytics company, Tableau Power BI consultancy, data analytics firm Delaware',
  },
  '/about/frederick-soh': {
    title: 'Frederick Soh, Founder of Pniel Analytics',
    description: 'Frederick Soh, founder of Pniel Analytics: 10 years in BI, Tableau Featured Author (2024), Tableau Certified Data Analyst and Power BI PL-300 certified.',
    keywords: 'Frederick Soh, Pniel Analytics founder, Tableau consultant, Power BI consultant, BI analytics expert, Tableau Public featured author',
    author: 'Frederick Soh',
    ogType: 'profile',
  },
  '/dashboards': {
    title: 'Featured Tableau Dashboards | Pniel Analytics',
    description: 'Explore live, interactive Tableau dashboards across finance, healthcare, executive reporting, and the public sector. See our BI work in action.',
    keywords: 'Tableau dashboard examples, interactive dashboards, BI dashboard portfolio, Tableau Public, executive dashboard examples, Pniel Analytics dashboards',
  },
  '/case-studies': {
    title: 'Analytics & BI Case Studies | Pniel Analytics',
    description: 'Tableau and Power BI case studies in healthcare, finance, e-commerce, manufacturing, nonprofits and public safety. See the data, the build and the results.',
    keywords: 'analytics case studies, BI case studies, Tableau case study, Power BI case study, dashboard case studies, data analytics projects, Pniel Analytics',
  },
  '/insights': {
    title: 'Insights: BI, Tableau & Analytics Articles | Pniel Analytics',
    description: 'Practical articles on business intelligence, Tableau, Power BI, dashboards, and analytics strategy. Turn scattered data into confident decisions.',
    keywords: 'business intelligence blog, analytics insights, Tableau tips, Power BI articles, dashboard best practices, data strategy, Pniel Analytics',
  },
  '/analytics-assessment': {
    title: 'Free 30-Minute Analytics Assessment | Pniel Analytics',
    description: 'Book a free 30-minute analytics assessment: a review of your data and reporting, the quick wins and gaps, and a clear next step. No obligation.',
    keywords: 'free analytics assessment, data assessment, BI consultation, Tableau consultation, Power BI consultation, analytics strategy call, Pniel Analytics',
  },
  '/testimonials': {
    title: 'Client Testimonials & Success Stories | Pniel Analytics',
    description: 'Read what clients say about working with Pniel Analytics on business intelligence, dashboards, and data strategy engagements.',
    keywords: 'Pniel Analytics reviews, client testimonials, analytics consulting reviews, Tableau consulting testimonials',
  },
  '/submit-testimonial': {
    title: 'Share Your Experience | Pniel Analytics',
    description: 'Submit a testimonial about your experience working with Pniel Analytics.',
    keywords: 'submit testimonial, Pniel Analytics feedback, client review',
  },
  '/terms': {
    title: 'Terms & Conditions: Site Use & Policies | Pniel Analytics',
    description: 'Review Pniel Analytics LLC Terms & Conditions covering website use, intellectual property, disclaimers, access, limitations, and your legal responsibilities.',
    keywords: 'Pniel Analytics terms and conditions, website terms',
  },
  '/privacy': {
    title: 'Privacy Policy: Your Data & Cookies | Pniel Analytics',
    description: 'Read Pniel Analytics LLC’s Privacy Policy to learn how we collect, use, share, protect, and manage your personal information, cookies, and analytics data.',
    keywords: 'Pniel Analytics privacy policy, data privacy',
  },
  '/eeo': {
    title: 'Equal Employment Opportunity | Pniel Analytics',
    description: 'Learn about Pniel Analytics LLC’s Equal Employment Opportunity, non-discrimination, anti-harassment, and reasonable accommodation policies for all applicants.',
    keywords: 'equal employment opportunity, EEO statement, Pniel Analytics careers',
  },
  '/ada': {
    title: 'Accessibility Statement (ADA) | Pniel Analytics',
    description: 'Pniel Analytics LLC is committed to digital accessibility for people with disabilities in line with the ADA and WCAG.',
    keywords: 'accessibility statement, ADA compliance, web accessibility',
  },
};

const DEFAULT_SEO = {
  title: 'Pniel Analytics | Business Intelligence & Analytics',
  description: 'Pniel Analytics builds Tableau and Power BI systems for data-driven decisions.',
  keywords: DEFAULT_KEYWORDS,
};

function upsertMeta(name, content) {
  // Non-production hosts (raw deployment host, previews) must never be indexed —
  // the canonical, indexable site is pnielanalytics.com only.
  if (name === 'robots') {
    const h = window.location.hostname;
    if (h !== 'pnielanalytics.com' && h !== 'localhost' && h !== '127.0.0.1') {
      content = 'noindex, nofollow';
    }
  }
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertProperty(property, content) {
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.replace(/\/+$/, '') || '/';
    // Article detail pages manage their own SEO (title, meta, canonical, JSON-LD)
    // client-side after fetching, so skip them here to avoid clobbering.
    if (path.startsWith('/insights/')) return;
    let seo = PAGE_SEO[path];
    let robots = 'index, follow';
    if (!seo && path.startsWith('/case-studies/')) {
      const slug = path.slice('/case-studies/'.length);
      const cs = mockData.caseStudies.find((s) => s.slug === slug);
      if (cs) {
        seo = {
          title: cs.seoTitle || `${cs.title} | Pniel Analytics`,
          description: cs.description,
          keywords: `${cs.industry} analytics case study, ${(cs.tools || []).join(', ')}, dashboard case study, Pniel Analytics`,
        };
      }
    }
    seo = seo || DEFAULT_SEO;
    // Unknown routes (soft-404) and admin must not be indexed.
    if (path.startsWith('/admin')) {
      robots = 'noindex, nofollow';
    } else if (seo === DEFAULT_SEO && path !== '/') {
      robots = 'noindex, follow';
      seo = { title: 'Page Not Found | Pniel Analytics', description: 'The page you are looking for could not be found. Explore Pniel Analytics services, dashboards, and case studies instead.' };
    }
    const canonicalHref = SITE_URL + (path === '/' ? '/' : path);

    document.title = seo.title;
    upsertMeta('description', seo.description);
    upsertMeta('robots', robots);
    upsertMeta('publisher', PUBLISHER);
    upsertMeta('author', seo.author || PUBLISHER);
    // meta keywords intentionally not emitted (ignored by search engines).
    const staleKeywords = document.head.querySelector('meta[name="keywords"]');
    if (staleKeywords) staleKeywords.remove();

    // Open Graph + Twitter
    upsertProperty('og:type', seo.ogType || 'website');
    upsertProperty('og:site_name', 'Pniel Analytics');
    upsertProperty('og:title', seo.title);
    upsertProperty('og:description', seo.description);
    upsertProperty('og:url', canonicalHref);
    upsertProperty('og:image', OG_IMAGE);
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', seo.title);
    upsertMeta('twitter:description', seo.description);
    upsertMeta('twitter:image', OG_IMAGE);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalHref);
  }, [pathname]);

  return null;
}
